import type { MarketingDecision } from './marketingTypes';
import type { CharacterOperation } from './operationTypes';
import type {
  PlatformDecision,
  PlatformId,
} from './platformTypes';

export interface PlatformDecisionInput {
  strategy?: unknown;
  marketing: MarketingDecision;
  operation: CharacterOperation;
  today_index: number;
}

function uniquePlatforms(list: PlatformId[]): PlatformId[] {
  return [...new Set(list)];
}

function ruleRestCooldown(): PlatformDecision {
  return {
    primary_platform: null,
    secondary_platforms: [],
    recommended_platforms: [],
    platform_priority: 100,
    posting_style: 'life',
    interaction_style: 'none',
    commercial_style: 'none',
    visibility_mode: 'cooldown',
    cross_posting: false,
    platform_reason: ['营销处于休息模式，全部平台进入 cooldown。'],
  };
}

function ruleTrustPlatforms(): PlatformDecision {
  const recommended: PlatformId[] = ['xiaohongshu', 'threads', 'instagram'];
  return {
    primary_platform: 'xiaohongshu',
    secondary_platforms: ['threads', 'instagram'],
    recommended_platforms: recommended,
    platform_priority: 90,
    posting_style: 'life',
    interaction_style: 'light',
    commercial_style: 'soft',
    visibility_mode: 'normal',
    cross_posting: true,
    platform_reason: ['信任日优先小红书，辅以 Threads 与 Instagram。'],
  };
}

function ruleInteractionPlatforms(): PlatformDecision {
  const recommended: PlatformId[] = ['threads', 'facebook'];
  return {
    primary_platform: 'threads',
    secondary_platforms: ['facebook'],
    recommended_platforms: recommended,
    platform_priority: 80,
    posting_style: 'conversation',
    interaction_style: 'reply_first',
    commercial_style: 'none',
    visibility_mode: 'boost',
    cross_posting: false,
    platform_reason: ['互动日优先 Threads，辅以 Facebook。'],
  };
}

function rulePromotionPlatforms(allowTiktok: boolean): PlatformDecision {
  const secondary: PlatformId[] = ['xiaohongshu'];
  const recommended: PlatformId[] = ['instagram', 'xiaohongshu'];
  if (allowTiktok) {
    secondary.push('tiktok');
    recommended.push('tiktok');
  }
  return {
    primary_platform: 'instagram',
    secondary_platforms: uniquePlatforms(secondary),
    recommended_platforms: uniquePlatforms(recommended),
    platform_priority: 70,
    posting_style: 'promotion',
    interaction_style: 'active',
    commercial_style: 'light',
    visibility_mode: 'normal',
    cross_posting: true,
    platform_reason: allowTiktok
      ? ['推广日优先 Instagram 与小红书，内容型态允许时加入 TikTok。']
      : ['推广日优先 Instagram 与小红书。'],
  };
}

function ruleFallbackThreads(): PlatformDecision {
  return {
    primary_platform: 'threads',
    secondary_platforms: [],
    recommended_platforms: ['threads'],
    platform_priority: 50,
    posting_style: 'life',
    interaction_style: 'light',
    commercial_style: 'none',
    visibility_mode: 'normal',
    cross_posting: false,
    platform_reason: ['使用基础平台节奏，默认 Threads。'],
  };
}

/**
 * 固定优先顺序：
 * 1. Marketing rest → 全部 cooldown
 * 2. Marketing trust（高商业回退到信任日）→ Xiaohongshu / Threads / Instagram
 * 3. Marketing interaction → Threads / Facebook
 * 4. Marketing promotion → Instagram / Xiaohongshu /（TikTok 若内容允许）
 * 5. Fallback → Threads
 *
 * 注意：MarketingBrain 的 base fallback 也是 marketing_mode=trust（priority=50），
 * 该情况走 Platform Fallback，不走 Rule 2（Rule 2 仅对应高商业→信任，priority=90）。
 */
export function evaluatePlatformRules(
  marketing: MarketingDecision,
  operation: CharacterOperation,
): PlatformDecision {
  if (marketing.marketing_mode === 'rest') {
    return ruleRestCooldown();
  }

  if (marketing.marketing_mode === 'trust' && marketing.marketing_priority >= 90) {
    return ruleTrustPlatforms();
  }

  if (marketing.marketing_mode === 'interaction') {
    return ruleInteractionPlatforms();
  }

  if (marketing.marketing_mode === 'promotion' && marketing.promotion_allowed) {
    const allowTiktok =
      operation.today_content_mode === 'promotion' ||
      marketing.promotion_level === 'medium';
    return rulePromotionPlatforms(allowTiktok);
  }

  return ruleFallbackThreads();
}
